import { JSONSchema7Array, JSONSchema7Definition } from 'json-schema';
import { defaults as _defaults } from 'lodash';
import { FormOption } from '../formOption';
import {
    FormField,
    FormFieldConfig,
    formFieldDefaultConfig,
} from './formField';

export interface FormFieldWithOptionsConfig<TValue, TOptionKey>
    extends FormFieldConfig<TValue> {
    // When options are simply defined statically
    options?: FormOption<TOptionKey>[];

    // When options are dynamically defined through external means
    // DEV NOTE: The setter function may not currently be called by all form fields with options,
    // as they do not necessarily share a vue component base! (check FormSelect.vue for an already existing implementation)
    hasServiceDrivenOptions?: boolean;
    /** Function by which a list of options can be determined */
    onMountOptionsSetter?:
        | (() => Promise<FormOption<TOptionKey>[]>)
        | undefined;
}

export const formFieldWithOptionsDefaultConfig = {
    ...formFieldDefaultConfig,
    options: [],
    hasServiceDrivenOptions: false,
    onMountOptionsSetter: undefined,
};

// eslint-disable-next-line @typescript-eslint/comma-dangle
export class FormFieldWithOptions<
    TValue,
    TOptionKey
> extends FormField<TValue | null> {
    protected valueIsArrayType: boolean;
    public options: FormOption<TOptionKey>[];
    public hasServiceDrivenOptions: boolean;
    public onMountOptionsSetter:
        | (() => Promise<FormOption<TOptionKey>[]>)
        | undefined;

    public constructor(config: FormFieldWithOptionsConfig<TValue, TOptionKey>) {
        const resolvedConfig = _defaults(
            config || ({} as FormFieldWithOptionsConfig<TValue, TOptionKey>),
            formFieldWithOptionsDefaultConfig,
        );
        super(resolvedConfig);

        this.valueIsArrayType = false;
        this.options = resolvedConfig.options;
        this.onMountOptionsSetter = resolvedConfig.onMountOptionsSetter;
        this.hasServiceDrivenOptions =
            resolvedConfig.hasServiceDrivenOptions || false;
    }

    public getJsonSchema(): JSONSchema7Definition {
        if (!this.options.length || this.hasServiceDrivenOptions) {
            return {
                type: 'string',
            };
        }

        const optionEnum = (Array.from(
            this.options.map((o) => o.key) ?? [],
        ) as unknown) as JSONSchema7Array;

        if (this.valueIsArrayType) {
            return {
                type: 'array',
                items: {
                    enum: optionEnum,
                },
            };
        }

        return {
            enum: optionEnum,
        };
    }
}
